// @ts-check
// Optional account system: Firebase Authentication + Firestore profile (username, skin).
// Fully optional — nothing in this file is required for singleplayer/offline play.
// Responsibilities are intentionally kept separate from world storage:
//   Firebase Auth      -> account identity (email/password, session)
//   Firestore          -> username, skin, profile/settings
//   localStorage        -> local/offline "no account" skin fallback (see localProfile below)
//   Google Drive (later) -> optional cloud world storage, unrelated to this file
import { proxy, subscribe } from 'valtio';

// Public Firebase web config. This is safe to ship client-side (same as googledrive.js's
// OAuth client id above) — Firebase security comes from Firestore/Auth rules, not from
// keeping this object secret.
const firebaseConfig = {
  apiKey: 'AIzaSyAdEXDj-YibAi7k9UkLutUmrAxAvh6upuk',
  authDomain: 'minecraft--login.firebaseapp.com',
  projectId: 'minecraft--login',
  storageBucket: 'minecraft--login.firebasestorage.app',
  messagingSenderId: '890380583832',
  appId: '1:890380583832:web:1667e6eeb2a040b6b461ed',
  measurementId: 'G-7VH1L67G0G',
};

export const isAccountsAvailable = () => {
  return !!firebaseConfig.apiKey;
};

let firebaseAppPromise = null;
const loadFirebase = async () => {
  if (!firebaseAppPromise) {
    firebaseAppPromise = (async () => {
      const [{ initializeApp }, authMod, firestoreMod] = await Promise.all([
        import('firebase/app'),
        import('firebase/auth'),
        import('firebase/firestore'),
      ]);
      const app = initializeApp(firebaseConfig);
      const auth = authMod.getAuth(app);
      const db = firestoreMod.getFirestore(app);
      return { app, auth, db, authMod, firestoreMod };
    })();
  }
  return firebaseAppPromise;
};

// ---- Reactive account state ----------------------------------------------------------

export const accountState = proxy(
  /** @type {{
   *   status: 'signed-out' | 'loading' | 'signed-in',
   *   uid: string | null,
   *   username: string | null,
   *   skinId: string | null,
   *   customSkinUrl: string | null,
   *   error: string | null,
  }} */ ({
    status: 'loading',
    uid: null,
    username: null,
    skinId: null,
    customSkinUrl: null,
    error: null,
  })
);

// ---- Local (no-account) skin fallback -------------------------------------------------
// Used when the player is not signed in, so they can still pick a skin. Structured as its
// own small object so it's trivial to keep, ignore, or migrate into a Firestore profile
// once the player creates an account.
export const localProfile = proxy({
  skinId: localStorage.localSkinId || 'steve',
});
subscribe(localProfile, () => {
  localStorage.localSkinId = localProfile.skinId;
});

// ---- Username validation ---------------------------------------------------------------

const USERNAME_RE = /^[a-zA-Z0-9_]{3,16}$/;

export const validateUsername = (username) => {
  if (!username || typeof username !== 'string') return 'Username is required.';
  if (username.length < 3 || username.length > 16) return 'Username must be 3-16 characters.';
  if (!USERNAME_RE.test(username)) return 'Only letters, numbers, and underscores are allowed.';
  return null;
};

const usernameKey = (username) => username.toLowerCase();

// ---- Auth lifecycle ---------------------------------------------------------------------

let unsubProfile = null;
let initialized = false;

export const initAccountSystem = async () => {
  if (initialized || !isAccountsAvailable()) {
    if (!isAccountsAvailable()) accountState.status = 'signed-out';
    return;
  }
  initialized = true;
  const { auth, db, authMod, firestoreMod } = await loadFirebase();
  authMod.onAuthStateChanged(auth, async (user) => {
    if (unsubProfile) {
      unsubProfile();
      unsubProfile = null;
    }
    if (!user) {
      accountState.status = 'signed-out';
      accountState.uid = null;
      accountState.username = null;
      accountState.skinId = null;
      accountState.customSkinUrl = null;
      return;
    }
    accountState.status = 'signed-in';
    accountState.uid = user.uid;
    const profileRef = firestoreMod.doc(db, 'profiles', user.uid);
    unsubProfile = firestoreMod.onSnapshot(profileRef, (snap) => {
      const data = snap.data();
      accountState.username = data?.username ?? null;
      accountState.skinId = data?.skinId ?? 'steve';
      accountState.customSkinUrl = data?.customSkinUrl ?? null;
    });
  });
};

/**
 * Create an account: Firebase Auth user + a race-safe unique username claim in Firestore.
 * Username uniqueness is enforced by creating a doc at usernames/{lowercaseUsername} inside
 * the SAME transaction that creates the profile doc. Firestore transactions fail atomically
 * if the usernames/{key} doc already exists, so two concurrent signups for the same name
 * cannot both succeed — one transaction will retry/fail and the caller sees a clear error.
 */
export const createAccount = async (email, password, username) => {
  const usernameError = validateUsername(username);
  if (usernameError) throw new Error(usernameError);

  const { auth, db, authMod, firestoreMod } = await loadFirebase();
  const key = usernameKey(username);

  // Pre-check for a fast, friendly error message. This is NOT the source of truth for
  // uniqueness (see the transaction below) — it just avoids making a user fill out the
  // whole form only to hit a race at the very end.
  const preCheckRef = firestoreMod.doc(db, 'usernames', key);
  const preCheckSnap = await firestoreMod.getDoc(preCheckRef);
  if (preCheckSnap.exists()) {
    throw new Error('That username is already taken.');
  }

  const credential = await authMod.createUserWithEmailAndPassword(auth, email, password);
  const uid = credential.user.uid;

  try {
    await firestoreMod.runTransaction(db, async (tx) => {
      const usernameRef = firestoreMod.doc(db, 'usernames', key);
      const usernameSnap = await tx.get(usernameRef);
      if (usernameSnap.exists()) {
        throw new Error('USERNAME_TAKEN');
      }
      const profileRef = firestoreMod.doc(db, 'profiles', uid);
      // usernames/{key} holds only the uid it maps to, and is immutable once created —
      // this is what makes the claim race-safe and the username permanent.
      tx.set(usernameRef, { uid, createdAt: firestoreMod.serverTimestamp() });
      tx.set(profileRef, {
        username,
        usernameLower: key,
        skinId: 'steve',
        customSkinUrl: null,
        createdAt: firestoreMod.serverTimestamp(),
      });
    });
  } catch (err) {
    // Roll back the auth account so we don't leave an orphaned user with no profile/username.
    await credential.user.delete().catch(() => {});
    if (err instanceof Error && err.message === 'USERNAME_TAKEN') {
      throw new Error('That username is already taken.');
    }
    throw err;
  }

  return credential.user;
};

export const signIn = async (email, password) => {
  const { auth, authMod } = await loadFirebase();
  await authMod.signInWithEmailAndPassword(auth, email, password);
};

export const signOutAccount = async () => {
  const { auth, authMod } = await loadFirebase();
  await authMod.signOut(auth);
};

export const setAccountSkin = async (skinId, customSkinUrl = null) => {
  if (accountState.status !== 'signed-in' || !accountState.uid) {
    // Not signed in: fall back to the local (no-account) profile so the picker still works.
    localProfile.skinId = skinId;
    return;
  }
  const { db, firestoreMod } = await loadFirebase();
  const profileRef = firestoreMod.doc(db, 'profiles', accountState.uid);
  await firestoreMod.updateDoc(profileRef, { skinId, customSkinUrl });
  accountState.skinId = skinId;
  accountState.customSkinUrl = customSkinUrl;
};

/**
 * Resolves the skin the player is currently using, whether signed in or not, as a
 * { skinId, url } pair ready to hand to the renderer.
 */
export const getActiveSkinId = () => {
  if (accountState.status === 'signed-in' && accountState.skinId) return accountState.skinId;
  return localProfile.skinId;
};
